import { UploadProvider } from './utils/uploadContext';
import UploadIndicator from './components/UploadIndicator';
import React, { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import LandingPage from './components/LandingPage';
import { LoginPage, SignupPage } from './components/AuthPages';
import { PrivacyPolicy, TermsOfService, ReturnPolicy } from './components/LegalPages';

import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import ChannelManager from './components/ChannelManager';
import VideoUpload from './components/VideoUpload';
import VideoLibrary from './components/VideoLibrary';
import ScheduleManager from './components/ScheduleManager';
import Analytics from './components/Analytics';
import ClaudeAI from './components/ClaudeAI';
import GrowthGuide from './components/GrowthGuide';
import VideoScorer from './components/VideoScorer';
import GuardianAI from './components/GuardianAI';
import ChannelVideos from './components/ChannelVideos';
import ContentForge from './components/ContentForge';
import AdminPanel from './components/AdminPanel';
import ThemeProvider from './components/ThemeProvider';
import ThemeSwitcher from './components/ThemeSwitcher';

const TIER_ACCESS = {
  none: [],
  starter: ['dashboard', 'upload', 'library', 'schedules'],
  creator: ['dashboard', 'upload', 'library', 'schedules', 'analytics', 'channels', 'obsidian'],
  pro: ['dashboard', 'upload', 'library', 'schedules', 'analytics', 'channels', 'obsidian', 'scorer', 'thumbnail', 'contentforge'],
  agency: ['dashboard', 'upload', 'library', 'schedules', 'analytics', 'channels', 'obsidian', 'scorer', 'thumbnail', 'contentforge', 'guardian', 'channel_videos', 'growth'],
  enterprise: ['dashboard', 'upload', 'library', 'schedules', 'analytics', 'channels', 'obsidian', 'scorer', 'thumbnail', 'contentforge', 'guardian', 'channel_videos', 'growth']
};

export default function App() {
  const [page, setPage] = useState(null);
  const [activePage, setActivePage] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem('vantage_token');
    const savedUser = sessionStorage.getItem('vantage_user');
    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setPage('dashboard');
      } catch (e) {
        sessionStorage.removeItem('vantage_token');
        sessionStorage.removeItem('vantage_user');
        setPage('landing');
      }
    } else {
      setPage('landing');
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setPage('dashboard');
    setActivePage('dashboard');
  };

  const handleLogout = () => {
    sessionStorage.removeItem('vantage_token');
    sessionStorage.removeItem('vantage_user');
    setUser(null);
    setPage('landing');
  };

  const hasAccess = (feature) => {
    if (!user) return false;
    if (user.is_admin) return true;
    const tier = user.subscription_tier || 'none';
    const allowed = TIER_ACCESS[tier] || [];
    return allowed.includes(feature);
  };

  const renderDashboardPage = () => {
    if (!hasAccess(activePage) && !user?.is_admin) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '60px', marginBottom: '24px' }}>🔒</div>
          <h2 style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-primary)', marginBottom: '12px' }}>Feature Locked</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '15px', marginBottom: '24px', maxWidth: '400px', lineHeight: '1.7' }}>
            This feature is not included in your current plan. Upgrade your subscription to unlock it.
          </p>
          <div style={{ padding: '16px 24px', borderRadius: '12px', background: 'rgba(0,200,83,0.08)', border: '1px solid rgba(0,200,83,0.3)', fontSize: '14px', color: 'var(--primary)', fontWeight: '600' }}>
            Contact support@vantagestudioai.com to upgrade your plan
          </div>
        </div>
      );
    }

    switch (activePage) {
      case 'dashboard': return <Dashboard setActivePage={setActivePage} />;
      case 'channels': return <ChannelManager />;
      case 'upload': return <VideoUpload />;
      case 'library': return <VideoLibrary />;
      case 'schedules': return <ScheduleManager />;
      case 'analytics': return <Analytics />;
      case 'obsidian': return <ClaudeAI />;
      case 'growth': return <GrowthGuide />;
      case 'scorer': return <VideoScorer />;
      case 'guardian': return <GuardianAI />;
      case 'channel_videos': return <ChannelVideos />;
      case 'contentforge': return <ContentForge />;
      case 'admin': return <AdminPanel />;
      default: return <Dashboard setActivePage={setActivePage} />;
    }
  };

  if (loading || page === null) {
    return (
      <div style={{ minHeight: '100vh', background: '#080e1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '3px solid rgba(0,200,83,0.2)', borderTop: '3px solid #00c853', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (page === 'landing') return <><LandingPage onLogin={() => setPage('login')} onSignup={() => setPage('signup')} onPrivacy={() => setPage('privacy')} onTerms={() => setPage('terms')} onReturn={() => setPage('return')} /><ToastContainer theme="dark" /></>;
  if (page === 'login') return <><LoginPage onLogin={handleLogin} onSwitchToSignup={() => setPage('signup')} onBack={() => setPage('landing')} /><ToastContainer theme="dark" /></>;
  if (page === 'signup') return <><SignupPage onSignup={handleLogin} onSwitchToLogin={() => setPage('login')} onBack={() => setPage('landing')} /><ToastContainer theme="dark" /></>;
  if (page === 'privacy') return <><PrivacyPolicy onBack={() => setPage('landing')} /><ToastContainer theme="dark" /></>;
  if (page === 'terms') return <><TermsOfService onBack={() => setPage('landing')} /><ToastContainer theme="dark" /></>;
  if (page === 'return') return <><ReturnPolicy onBack={() => setPage('landing')} /><ToastContainer theme="dark" /></>;

  if (page === 'dashboard') {
    return (
      <UploadProvider>
      <ThemeProvider>
        <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-main)' }}>
          <Sidebar
            activePage={activePage}
            setActivePage={setActivePage}
            user={user}
            onLogout={handleLogout}
            hasAccess={hasAccess}
          />
          <main style={{ flex: 1, overflow: 'auto', background: 'var(--bg-main)', minHeight: '100vh' }}>
            {!user?.is_active && (
              <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', padding: '12px 24px', fontSize: '13px', color: '#f59e0b', textAlign: 'center', fontWeight: '600' }}>
                ⚠️ Your account is pending activation. Send your payment screenshot to support@vantagestudioai.com or WhatsApp 03114608845
              </div>
            )}
            {renderDashboardPage()}
          </main>
          <ThemeSwitcher />
        </div>
        <ToastContainer theme="dark" />
        <UploadIndicator onNavigateToUpload={() => setActivePage('upload')} />
      </ThemeProvider>
      </UploadProvider>
    );
  }

  return null;
}